import type { ChatState, ChatAction } from './chatTypes';
import { initialChatState } from './chatTypes';

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_CLIENT':
      return { ...state, client: action.payload };

    case 'SET_INITIALIZING':
      return { ...state, isInitializing: action.payload };

    case 'SET_INITIALIZED':
      return { ...state, isInitialized: action.payload, initProgress: null };

    case 'SET_INIT_ERROR':
      return { ...state, initError: action.payload, isInitializing: false, initProgress: null };

    case 'SET_INIT_PROGRESS':
      return { ...state, initProgress: action.payload };

    case 'SET_HAS_CLOUD_BACKUP':
      return { ...state, hasCloudBackup: action.payload };

    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };

    case 'ADD_CONVERSATION': {
      // Avoid duplicates
      const exists = state.conversations.some((c) => c.id === action.payload.id);
      if (exists) return state;
      return {
        ...state,
        conversations: [action.payload, ...state.conversations],
      };
    }

    case 'UPDATE_CONVERSATION_LAST_MESSAGE': {
      const { id, text, time, sender } = action.payload;
      const updated = state.conversations.map((c) =>
        c.id === id
          ? { ...c, lastMessageText: text, lastMessageTime: time, lastMessageSender: sender }
          : c
      );
      // Re-sort by last message time (most recent first)
      updated.sort((a, b) => {
        const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
        const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
        return timeB - timeA;
      });
      return { ...state, conversations: updated };
    }

    case 'UPDATE_CONVERSATION_MEMBERS': {
      const { id, members } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === id ? { ...c, members } : c
        ),
      };
    }

    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversationId: action.payload };

    case 'SET_MESSAGES':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.conversationId]: action.payload.messages,
        },
      };

    case 'APPEND_MESSAGE': {
      const { conversationId, message } = action.payload;
      const existing = state.messages[conversationId] || [];
      // Avoid duplicate messages
      if (existing.some((m) => m.id === message.id)) return state;
      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: [...existing, message],
        },
      };
    }

    case 'PREPEND_MESSAGES': {
      const { conversationId, messages } = action.payload;
      const existing = state.messages[conversationId] || [];
      const existingIds = new Set(existing.map((m) => m.id));
      const newMessages = messages.filter((m) => !existingIds.has(m.id));
      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: [...newMessages, ...existing],
        },
      };
    }

    case 'SET_LOADING_MESSAGES':
      return {
        ...state,
        loadingMessages: {
          ...state.loadingMessages,
          [action.payload.conversationId]: action.payload.loading,
        },
      };

    case 'SET_HAS_MORE_MESSAGES':
      return {
        ...state,
        hasMoreMessages: {
          ...state.hasMoreMessages,
          [action.payload.conversationId]: action.payload.hasMore,
        },
      };

    case 'INCREMENT_UNREAD': {
      const convId = action.payload;
      const newCount = (state.unreadCounts[convId] || 0) + 1;
      return {
        ...state,
        unreadCounts: { ...state.unreadCounts, [convId]: newCount },
        totalUnread: state.totalUnread + 1,
      };
    }

    case 'SET_UNREAD_COUNT': {
      const { conversationId, count } = action.payload;
      const oldCount = state.unreadCounts[conversationId] || 0;
      return {
        ...state,
        unreadCounts: { ...state.unreadCounts, [conversationId]: count },
        totalUnread: Math.max(0, state.totalUnread - oldCount + count),
      };
    }

    case 'CLEAR_UNREAD': {
      const convId = action.payload;
      const cleared = state.unreadCounts[convId] || 0;
      return {
        ...state,
        unreadCounts: { ...state.unreadCounts, [convId]: 0 },
        totalUnread: Math.max(0, state.totalUnread - cleared),
      };
    }

    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };

    case 'SET_SYNCING':
      return { ...state, isSyncing: action.payload };

    case 'SET_CONVERSATION_FILTER':
      return { ...state, conversationFilter: action.payload };

    case 'SET_INSTALLATION_LIMIT':
      return {
        ...state,
        showInstallationManager: true,
        pendingClient: action.payload.client,
        pendingInboxId: action.payload.inboxId,
        isInitializing: false,
        initProgress: null,
      };

    case 'CLEAR_INSTALLATION_LIMIT':
      return { ...state, showInstallationManager: false, pendingClient: null, pendingInboxId: null };

    case 'RESET':
      return initialChatState;

    default:
      return state;
  }
}
